/**
 * Domain detection and URL generation utilities
 * Supports multi-domain architecture: pontis.life (public) and app.pontis.life (app)
 */

export const DOMAINS = {
  PUBLIC: 'pontis.life',
  PUBLIC_WWW: 'www.pontis.life',
  APP: 'app.pontis.life',
} as const;

/**
 * Detect if current hostname is the public domain (pontis.life or www.pontis.life)
 */
export const isPublicDomain = (): boolean => {
  const hostname = window.location.hostname;
  return hostname === DOMAINS.PUBLIC || hostname === DOMAINS.PUBLIC_WWW;
};

/**
 * Detect if current hostname is the app domain (app.pontis.life or localhost)
 */
export const isAppDomain = (): boolean => {
  const hostname = window.location.hostname;
  // Localhost and 127.0.0.1 treated as app domain for development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return true;
  }
  return hostname === DOMAINS.APP;
};

/**
 * Generate a full URL to the app domain
 * @param path - Path to append (e.g., '/auth' or '/memorial/123')
 */
export const getAppUrl = (path: string = '/'): string => {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `https://${DOMAINS.APP}${normalizedPath}`;
};

/**
 * Generate a full URL to the public domain (canonical, non-www)
 * @param path - Path to append (e.g., '/search' or '/memorial/123')
 */
export const getPublicUrl = (path: string = '/'): string => {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const hostname = window.location.hostname;
  
  // For local development, use the public-preview route to simulate the public site
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return `/public-preview${normalizedPath === '/' ? '' : normalizedPath}`;
  }

  // For staging (Netlify preview), use /public-preview to show customer website
  if (hostname.includes('netlify.app')) {
    return `/public-preview${normalizedPath === '/' ? '' : normalizedPath}`;
  }

  return `https://${DOMAINS.PUBLIC}${normalizedPath}`;
};
